import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import * as React from 'react';

export interface PageHeaderProps {
  /**
   * The main heading text for the page
   */
  title: string;
  /**
   * Optional description text that appears below the title
   */
  description?: string;
  /**
   * Optional action components to be displayed on the right side of the header
   */
  actions?: React.ReactNode;
  /**
   * The size of the heading (follows Chakra UI heading sizes)
   * @default "2xl"
   */
  titleSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  /**
   * Custom color for the description text
   * @default "gray.600"
   */
  descriptionColor?: string;
  /**
   * Bottom margin for the header
   * @default {8}
   */
  marginBottom?: number | string;
  /**
   * Additional spacing between title and description
   * @default 1
   */
  titleDescriptionGap?: number | string;
  /**
   * Custom alignment for the actions
   * @default "end"
   */
  actionsAlignment?: 'start' | 'center' | 'end';
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  function PageHeader(props, ref) {
    const {
      title,
      description,
      actions,
      titleSize = '2xl',
      descriptionColor = 'gray.600',
      marginBottom = 6,
      titleDescriptionGap = 1,
      actionsAlignment = 'end',
    } = props;

    return (
      <Flex
        ref={ref}
        justifyContent="space-between"
        alignItems={actionsAlignment}
        mb={marginBottom}
        gap={4}
      >
        <Box>
          <Heading size={titleSize}>{title}</Heading>
          {description && (
            <Text color={descriptionColor} mt={titleDescriptionGap}>
              {description}
            </Text>
          )}
        </Box>
        {actions && <Box>{actions}</Box>}
      </Flex>
    );
  }
);
